import { IconInfoCircleOutline } from '@iwoca/icons';
import { AlertBox, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { RepaymentScheduleTable } from 'components/RepaymentScheduleTable/RepaymentScheduleTable';
import { useGetRCFRepaymentSummary } from 'lendingApi/lendingApiHooks';

import { InterestOnlyTable } from './InterestOnlyTable';
import { PostRepaymentFreePeriodExplainer } from './PostRepaymentFreePeriodExplainer';
import styles from './RCFRepaymentSchedule.module.css';

export const RCFRepaymentSchedule = ({
  offerId,
  maxAmount,
}: {
  offerId: string;
  maxAmount: number;
}) => {
  const { data, isLoading } = useGetRCFRepaymentSummary(offerId, maxAmount);

  const schedule = data?.data?.schedule ?? [];
  const summary = data?.data?.summary;

  const interestOnlySchedule = schedule.filter(
    (repayment) => repayment.principal === 0,
  );

  const principalRepaymentsSchedule = schedule.filter(
    (repayment) => repayment.principal > 0,
  );

  if (isLoading) {
    return (
      <div>
        <Spinner dataTestId="spinner" />
      </div>
    );
  }

  return (
    <div className={styles.sections}>
      <div>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{t`Repayment-free period`}</h3>
          <p className={styles.sectionDescription}>
            <Trans>
              You pay interest only on the outstanding loan amount for 12
              months.
            </Trans>
          </p>
        </div>
        <div className={styles.tableWrapper}>
          <InterestOnlyTable schedule={interestOnlySchedule} />
        </div>
      </div>

      <PostRepaymentFreePeriodExplainer />

      <div>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            <Trans>Amortisation phase</Trans>
          </h3>
          <p className={styles.sectionDescription}>
            <Trans>
              If you do not extend your repayment-free period, the outstanding
              loan amount will be repaid in 12 monthly instalments as scheduled.
            </Trans>
          </p>
        </div>
        {summary && (
          <RepaymentScheduleTable
            schedule={principalRepaymentsSchedule}
            summary={summary}
          />
        )}
      </div>

      <AlertBox variant="connect" className={styles.explainerPoint}>
        <IconInfoCircleOutline className={styles.icon} />

        <p className={styles.explainerText}>
          <Trans>
            <strong>Remember:</strong> you can make free repayments at any time
            and save on interest.
          </Trans>
        </p>
      </AlertBox>
    </div>
  );
};
